import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useCompetition } from '../contexts/CompetitionContext';
import {
  competitionRoot,
  competitionGroupsPath,
  adminBulkDeleteGames,
  adminFetchPendingResults,
  adminConfirmPendingResult,
  adminDismissPendingResult,
  adminRecalculateResults
} from '../services/competitionApi';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Calendar, Plus, Edit, Save, X, Lock, CheckCircle, FileText, Download, Filter, ChevronDown, Layers, Target, Trash2, CheckSquare, Square, RefreshCw } from 'lucide-react';
import dayjs from 'dayjs';
import { AdminLayout } from '../components/layout/AdminLayout';
import { Button } from '../components/ui/Button';
import { ScoreText } from '../components/ui/ScoreText';
import { DateInputDMY } from '../components/ui/DateInputDMY';
import { isoDateToDMYDisplay, parseGameDateForCsv } from '../utils/dateLocale';
import { getTeamDisplayName, teamMatchesName } from '../utils/teamDisplayName';

const AdminGames = () => {
  const { t, language, isRTL } = useLanguage();
  const { competitionSlug } = useCompetition();
  const navigate = useNavigate();
  const basePath = `/c/${competitionSlug}`;
  const gamesBase = () => `${competitionRoot(competitionSlug)}/games`;
  const [allGames, setAllGames] = useState([]);
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [selectedRound, setSelectedRound] = useState('all');
  const [availableRounds, setAvailableRounds] = useState([]);
  const [showCloseGamesOnly, setShowCloseGamesOnly] = useState(false);
  const [formData, setFormData] = useState({
    homeGroupId: '',
    awayGroupId: '',
    date: '',
    hour: '',
    round: '',
    isBettingLocked: false,
    isFinished: false,
    isCanceled: false,
    homeScore: '',
    awayScore: ''
  });
  const [uploading, setUploading] = useState(false);
  const [selectedGameIds, setSelectedGameIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const fileInputRef = useRef();
  const [showRoundForm, setShowRoundForm] = useState(false);
  const [showCloseRoundModal, setShowCloseRoundModal] = useState(false);
  const [roundFormData, setRoundFormData] = useState({
    roundName: '',
    games: Array(7).fill(null).map(() => ({
      homeGroupId: '',
      awayGroupId: '',
      date: '',
      hour: '',
      isBettingLocked: false
    }))
  });
  const [closeRoundData, setCloseRoundData] = useState({
    selectedRound: '',
    games: []
  });
  const [pendingResults, setPendingResults] = useState([]);
  const [pendingActionId, setPendingActionId] = useState(null);
  const [recalculating, setRecalculating] = useState(false);

  // Helper function to determine if a game is "close" (within next 5 days)
  const isGameClose = useCallback((game) => {
    if (!game || !game.date || !game.hour) return false;
    const gameTime = dayjs(`${game.date} ${game.hour}`);
    const now = dayjs();
    const daysDiff = gameTime.diff(now, 'days');
    return daysDiff <= 5 && daysDiff >= 0;
  }, []);

  // Helper function to get unique rounds from games
  const getUniqueRounds = useCallback((gamesData) => {
    if (!gamesData || !Array.isArray(gamesData)) return [];
    const rounds = [...new Set(gamesData.map(game => game?.round).filter(Boolean))];
    return rounds.sort();
  }, []);

  // Helper function to determine current round based on close games
  const getCurrentRound = useCallback((gamesData) => {
    if (!gamesData || !Array.isArray(gamesData)) return null;
    const closeGames = gamesData.filter(isGameClose);
    if (closeGames.length === 0) return null;
    
    // Find the most common round among close games
    const roundCounts = {};
    closeGames.forEach(game => {
      if (game?.round) {
        roundCounts[game.round] = (roundCounts[game.round] || 0) + 1;
      }
    });
    
    if (Object.keys(roundCounts).length === 0) return null;
    
    return Object.keys(roundCounts).reduce((a, b) => 
      roundCounts[a] > roundCounts[b] ? a : b
    );
  }, [isGameClose]);

  // Filter games based on selected round and close games setting
  const filterGames = useCallback((gamesData) => {
    if (!gamesData || !Array.isArray(gamesData)) return [];
    let filtered = gamesData;

    // Filter by round
    if (selectedRound === 'current') {
      const currentRound = getCurrentRound(gamesData);
      if (currentRound) {
        filtered = filtered.filter(game => game?.round === currentRound);
      } else if (showCloseGamesOnly) {
        filtered = filtered.filter(isGameClose);
      }
    } else if (selectedRound === 'all') {
      // Show all games
    } else {
      // Show specific round
      filtered = filtered.filter(game => game?.round === selectedRound);
    }

    // Apply close games filter if enabled and not already filtered by current round
    if (showCloseGamesOnly && selectedRound !== 'current') {
      filtered = filtered.filter(isGameClose);
    }

    return filtered;
  }, [selectedRound, showCloseGamesOnly, getCurrentRound, isGameClose]);

  useEffect(() => {
    fetchData();
    setSelectedGameIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionSlug]);

  useEffect(() => {
    if (allGames.length > 0) {
      const filtered = filterGames(allGames);
      setGames(filtered);
    }
  }, [allGames, filterGames]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [gamesRes, teamsRes, pending] = await Promise.all([
        axios.get(gamesBase()),
        axios.get(competitionGroupsPath(competitionSlug)),
        adminFetchPendingResults(competitionSlug).catch(() => [])
      ]);
      const gamesData = gamesRes.data;
      
      setAllGames(gamesData);
      setTeams(Array.isArray(teamsRes.data) ? teamsRes.data : []);
      setPendingResults(Array.isArray(pending) ? pending : []);
      
      // Get unique rounds and set up available rounds
      const rounds = getUniqueRounds(gamesData);
      const currentRound = getCurrentRound(gamesData);
      
      // Build available rounds array
      const roundOptions = [
        { value: 'all', label: language === 'en' ? 'All Rounds' : 'כל הסיבובים' }
      ];
      
      if (currentRound) {
        roundOptions.push({ value: 'current', label: language === 'en' ? 'Current Round' : 'סיבוב נוכחי' });
      }
      
      rounds.forEach(round => {
        roundOptions.push({ value: round, label: round });
      });
      
      setAvailableRounds(roundOptions);
      
      // Set initial filtered games
      const filtered = filterGames(gamesData);
      setGames(filtered);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(language === 'en' ? 'Failed to load games and teams' : 'נכשל בטעינת משחקים וקבוצות');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate scores if provided (both should be numbers or both should be empty)
    const homeScoreProvided = formData.homeScore !== '';
    const awayScoreProvided = formData.awayScore !== '';
    const kickoff = formData.date && formData.hour
      ? dayjs(`${formData.date} ${formData.hour}`)
      : null;
    const kickoffIsPast = kickoff ? kickoff.isBefore(dayjs(), 'minute') : false;
    
    if (formData.isFinished && kickoffIsPast && (!homeScoreProvided || !awayScoreProvided)) {
      toast.error(language === 'en' ? 'Both home and away scores are required for finished games' : 'נדרשות תוצאות של שתי הקבוצות למשחקים שהסתיימו');
      return;
    }
    
    if ((homeScoreProvided || awayScoreProvided)) {
      if (!homeScoreProvided || !awayScoreProvided) {
        toast.error(language === 'en' ? 'Please provide both home and away scores or leave both empty' : 'אנא הזן את שתי התוצאות או השאר את שתיהן ריקות');
        return;
      }
      
      if (!/^\d+$/.test(formData.homeScore) || !/^\d+$/.test(formData.awayScore)) {
        toast.error(language === 'en' ? 'Scores must be valid numbers' : 'התוצאות חייבות להיות מספרים תקינים');
        return;
      }
    }
    
    try {
      if (editingGame) {
        await axios.put(`${gamesBase()}/${editingGame._id}`, {
          homeGroupId: formData.homeGroupId,
          awayGroupId: formData.awayGroupId,
          date: formData.date,
          hour: formData.hour,
          round: formData.round
        });

        const shouldSubmitResult =
          homeScoreProvided &&
          awayScoreProvided &&
          (formData.isFinished || kickoffIsPast);

        if (shouldSubmitResult) {
          await axios.put(`${gamesBase()}/${editingGame._id}/result`, {
            finalResult: `${formData.homeScore}:${formData.awayScore}`
          });
        }

        toast.success(language === 'en' ? 'Game updated successfully!' : 'המשחק עודכן בהצלחה!');
      } else {
        const submitData = {
          homeGroupId: formData.homeGroupId,
          awayGroupId: formData.awayGroupId,
          date: formData.date,
          hour: formData.hour,
          round: formData.round
        };
        await axios.post(gamesBase(), submitData);
        toast.success(language === 'en' ? 'Game created successfully!' : 'המשחק נוצר בהצלחה!');
      }
      
      setShowForm(false);
      setEditingGame(null);
      resetForm();
      fetchData();
    } catch (error) {
      const message = error.response?.data?.message || (language === 'en' ? 'Failed to save game' : 'נכשל בשמירת המשחק');
      toast.error(message);
    }
  };

  const handleEdit = (game) => {
    setEditingGame(game);
    
    // Parse existing finalResult into separate scores (API uses object or legacy string)
    let homeScore = '';
    let awayScore = '';
    if (game.finalResult) {
      if (typeof game.finalResult === 'object' && game.finalResult !== null) {
        if ('homeScore' in game.finalResult && 'awayScore' in game.finalResult) {
          homeScore = String(game.finalResult.homeScore);
          awayScore = String(game.finalResult.awayScore);
        }
      } else if (typeof game.finalResult === 'string' && game.finalResult.includes(':')) {
        const scores = game.finalResult.split(':');
        homeScore = scores[0] || '';
        awayScore = scores[1] || '';
      }
    }
    
    setFormData({
      homeGroupId: game.homeGroupId._id || game.homeGroupId,
      awayGroupId: game.awayGroupId._id || game.awayGroupId,
      date: game.date,
      hour: game.hour,
      round: game.round || '',
      isBettingLocked: game.isBettingLocked,
      isFinished: game.isFinished,
      isCanceled: game.isCanceled || false,
      homeScore: homeScore,
      awayScore: awayScore
    });
    setShowForm(true);
  };

  const handleDelete = async (gameId) => {
    if (!window.confirm(language === 'en' ? 'Are you sure you want to delete this game?' : 'האם אתה בטוח שברצונך למחוק את המשחק הזה?')) return;
    
    try {
      await axios.delete(`${gamesBase()}/${gameId}`);
      toast.success(language === 'en' ? 'Game deleted successfully!' : 'המשחק נמחק בהצלחה!');
      setSelectedGameIds((prev) => prev.filter((id) => id !== gameId));
      fetchData();
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to delete game' : 'נכשל במחיקת המשחק');
    }
  };

  const toggleGameSelection = (gameId) => {
    setSelectedGameIds((prev) =>
      prev.includes(gameId) ? prev.filter((id) => id !== gameId) : [...prev, gameId]
    );
  };

  const allGamesSelected = games.length > 0 && selectedGameIds.length === games.length;

  const toggleSelectAllGames = () => {
    if (selectedGameIds.length === games.length) {
      setSelectedGameIds([]);
    } else {
      setSelectedGameIds(games.map((game) => game._id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedGameIds.length === 0) return;

    const count = selectedGameIds.length;
    const confirmMessage =
      language === 'en'
        ? `Delete ${count} selected game${count === 1 ? '' : 's'}? This cannot be undone.`
        : `למחוק ${count} משחקים שנבחרו? לא ניתן לבטל פעולה זו.`;

    if (!window.confirm(confirmMessage)) return;

    try {
      setBulkDeleting(true);
      const result = await adminBulkDeleteGames(competitionSlug, selectedGameIds);
      const deletedCount = result.deleted?.length || 0;
      const skippedCount = result.skipped?.length || 0;

      if (deletedCount > 0) {
        toast.success(
          language === 'en'
            ? `Deleted ${deletedCount} game${deletedCount === 1 ? '' : 's'}`
            : `נמחקו ${deletedCount} משחקים`
        );
      }
      if (skippedCount > 0) {
        toast.error(
          language === 'en'
            ? `${skippedCount} game${skippedCount === 1 ? '' : 's'} could not be deleted (has bets or not found)`
            : `${skippedCount} משחקים לא נמחקו (יש הימורים או לא נמצאו)`
        );
      }

      setSelectedGameIds([]);
      fetchData();
    } catch (error) {
      const message =
        error.response?.data?.message ||
        (language === 'en' ? 'Failed to delete selected games' : 'נכשל במחיקת המשחקים שנבחרו');
      toast.error(message);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleRoundFormChange = (gameIndex, field, value) => {
    const newGames = [...roundFormData.games];
    newGames[gameIndex] = { ...newGames[gameIndex], [field]: value };
    setRoundFormData({ ...roundFormData, games: newGames });
  };

  const handleRoundSubmit = async (e) => {
    e.preventDefault();
    
    if (!roundFormData.roundName.trim()) {
      toast.error(language === 'en' ? 'Please enter a round name' : 'אנא הזן שם סיבוב');
      return;
    }

    // Validate all games
    for (let i = 0; i < roundFormData.games.length; i++) {
      const game = roundFormData.games[i];
      if (!game.homeGroupId || !game.awayGroupId || !game.date || !game.hour) {
        toast.error(language === 'en' ? `Please fill all fields for game ${i + 1}` : `אנא מלא את כל השדות למשחק ${i + 1}`);
        return;
      }
      if (game.homeGroupId === game.awayGroupId) {
        toast.error(language === 'en' ? `Game ${i + 1}: Home and away teams cannot be the same` : `משחק ${i + 1}: קבוצות בית וחוץ לא יכולות להיות זהות`);
        return;
      }
    }

    try {
      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < roundFormData.games.length; i++) {
        const game = roundFormData.games[i];
        try {
          await axios.post(gamesBase(), {
            homeGroupId: game.homeGroupId,
            awayGroupId: game.awayGroupId,
            date: game.date,
            hour: game.hour,
            round: roundFormData.roundName.trim(),
            isBettingLocked: game.isBettingLocked
          });
          successCount++;
        } catch (error) {
          console.error(`Failed to create game ${i + 1}:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(language === 'en' ? `Successfully created ${successCount} game(s)!` : `נוצרו בהצלחה ${successCount} משחקים!`);
      }
      if (failCount > 0) {
        toast.error(language === 'en' ? `Failed to create ${failCount} game(s)` : `נכשל ביצירת ${failCount} משחקים`);
      }

      setShowRoundForm(false);
      setRoundFormData({
        roundName: '',
        games: Array(7).fill(null).map(() => ({
          homeGroupId: '',
          awayGroupId: '',
          date: '',
          hour: '',
          isBettingLocked: false
        }))
      });
      fetchData();
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to create round games' : 'נכשל ביצירת משחקי הסיבוב');
    }
  };

  const handleOpenCloseRound = () => {
    const rounds = getUniqueRounds(allGames);
    if (rounds.length === 0) {
      toast.error(language === 'en' ? 'No rounds available' : 'אין סיבובים זמינים');
      return;
    }
    setShowCloseRoundModal(true);
    setCloseRoundData({
      selectedRound: rounds[0] || '',
      games: []
    });
  };

  const handleRecalculateResults = async () => {
    const ok = window.confirm(
      language === 'en'
        ? 'Recalculate points for all finished games in this competition? Exact scores and the leaderboard will be updated.'
        : 'לחשב מחדש נקודות לכל המשחקים שהסתיימו בתחרות זו? תוצאות מדויקות וטבלת המובילים יעודכנו.'
    );
    if (!ok) return;
    try {
      setRecalculating(true);
      const summary = await adminRecalculateResults(competitionSlug);
      const changed = summary.betsChanged ?? 0;
      const exact = summary.exactScores ?? 0;
      toast.success(
        language === 'en'
          ? `Recalculated ${summary.betsProcessed ?? 0} bets (${changed} updated, ${exact} exact)`
          : `חושבו מחדש ${summary.betsProcessed ?? 0} הימורים (${changed} עודכנו, ${exact} מדויקים)`
      );
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          (language === 'en' ? 'Failed to recalculate points' : 'נכשל בחישוב מחדש של נקודות')
      );
    } finally {
      setRecalculating(false);
    }
  };

  const handleLoadRoundGames = async () => {
    if (!closeRoundData.selectedRound) {
      toast.error(language === 'en' ? 'Please select a round' : 'אנא בחר סיבוב');
      return;
    }

    try {
      const roundGames = allGames.filter(game => game.round === closeRoundData.selectedRound);
      if (roundGames.length === 0) {
        toast.error(language === 'en' ? 'No games found in this round' : 'לא נמצאו משחקים בסיבוב זה');
        return;
      }

      const gamesWithScores = roundGames.map(game => {
        let homeScore = '';
        let awayScore = '';
        if (game.finalResult) {
          if (typeof game.finalResult === 'object' && game.finalResult !== null) {
            if ('homeScore' in game.finalResult && 'awayScore' in game.finalResult) {
              homeScore = String(game.finalResult.homeScore);
              awayScore = String(game.finalResult.awayScore);
            }
          } else if (typeof game.finalResult === 'string' && game.finalResult.includes(':')) {
            const scores = game.finalResult.split(':');
            homeScore = scores[0] || '';
            awayScore = scores[1] || '';
          }
        }
        return {
          _id: game._id,
          homeTeam: typeof game.homeGroupId === 'object' ? game.homeGroupId.name : '',
          awayTeam: typeof game.awayGroupId === 'object' ? game.awayGroupId.name : '',
          homeScore,
          awayScore,
          isFinished: game.isFinished,
          isCanceled: game.isCanceled || false
        };
      });

      setCloseRoundData({ ...closeRoundData, games: gamesWithScores });
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to load round games' : 'נכשל בטעינת משחקי הסיבוב');
    }
  };

  const handleCloseRoundScoresChange = (gameIndex, field, value) => {
    const newGames = [...closeRoundData.games];
    newGames[gameIndex] = { ...newGames[gameIndex], [field]: value };
    setCloseRoundData({ ...closeRoundData, games: newGames });
  };

  const handleCloseRoundSubmit = async (e) => {
    e.preventDefault();
    
    if (closeRoundData.games.length === 0) {
      toast.error(language === 'en' ? 'Please load round games first' : 'אנא טען תחילה את משחקי הסיבוב');
      return;
    }

    // Validate all scores
    for (let i = 0; i < closeRoundData.games.length; i++) {
      const game = closeRoundData.games[i];
      const hs = game.homeScore;
      const as = game.awayScore;
      const hsProvided = hs !== undefined && hs !== '';
      const asProvided = as !== undefined && as !== '';

      if (game.isFinished && (!hsProvided || !asProvided)) {
        toast.error(language === 'en' ? `Game ${i + 1}: Both scores are required for finished games` : `משחק ${i + 1}: נדרשות שתי התוצאות למשחקים שהסתיימו`);
        return;
      }
      if ((hsProvided || asProvided) && (!hsProvided || !asProvided)) {
        toast.error(language === 'en' ? `Game ${i + 1}: Please provide both scores or leave both empty` : `משחק ${i + 1}: אנא הזן את שתי התוצאות או השאר את שתיהן ריקות`);
        return;
      }
      if ((hsProvided && !/^\d+$/.test(String(hs))) || (asProvided && !/^\d+$/.test(String(as)))) {
        toast.error(language === 'en' ? `Game ${i + 1}: Scores must be valid numbers` : `משחק ${i + 1}: התוצאות חייבות להיות מספרים תקינים`);
        return;
      }
    }

    try {
      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < closeRoundData.games.length; i++) {
        const game = closeRoundData.games[i];
        try {
          const hs = game.homeScore;
          const as = game.awayScore;
          const hsProvided = hs !== undefined && hs !== '';
          const asProvided = as !== undefined && as !== '';
          const submitData = {
            isFinished: game.isFinished,
            isCanceled: game.isCanceled || false,
            finalResult: (hsProvided && asProvided) ? `${game.homeScore}:${game.awayScore}` : ''
          };
          await axios.put(`${gamesBase()}/${game._id}/result`, submitData);
          successCount++;
        } catch (error) {
          console.error(`Failed to update game ${i + 1}:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(language === 'en' ? `Successfully updated ${successCount} game(s)!` : `עודכנו בהצלחה ${successCount} משחקים!`);
      }
      if (failCount > 0) {
        toast.error(language === 'en' ? `Failed to update ${failCount} game(s)` : `נכשל בעדכון ${failCount} משחקים`);
      }

      setShowCloseRoundModal(false);
      setCloseRoundData({
        selectedRound: '',
        games: []
      });
      fetchData();
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to update round scores' : 'נכשל בעדכון תוצאות הסיבוב');
    }
  };

  const resetForm = () => {
    setFormData({
      homeGroupId: '',
      awayGroupId: '',
      date: '',
      hour: '',
      round: '',
      isBettingLocked: false,
      isFinished: false,
      isCanceled: false,
      homeScore: '',
      awayScore: ''
    });
  };

  const findTeamByName = (teamName) => {
    return teams.find((team) => teamMatchesName(team, teamName));
  };

  const findTeamByIdOrName = (identifier) => {
    const cleanIdentifier = identifier.trim();
    
    // First try to find by ID (if it's a valid MongoDB ObjectId pattern - 24 hex characters)
    const objectIdPattern = /^[0-9a-fA-F]{24}$/;
    if (objectIdPattern.test(cleanIdentifier)) {
      const teamById = teams.find(team => team._id === cleanIdentifier);
      if (teamById) return teamById;
    }
    
    // Try to find by partial ID match (for shorter IDs like 687acdc7)
    const partialIdPattern = /^[0-9a-fA-F]{8,}$/;
    if (partialIdPattern.test(cleanIdentifier)) {
      const teamByPartialId = teams.find(team => team._id.startsWith(cleanIdentifier));
      if (teamByPartialId) return teamByPartialId;
    }
    
    // If not found by ID or partial ID, try to find by name
    return findTeamByName(identifier);
  };

  // Helper function to parse CSV lines with proper quote handling
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          // Escaped quote
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    // Add the last field
    result.push(current.trim());
    return result;
  };

  const handleCSVUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error(language === 'en' ? 'Please select a CSV file' : 'אנא בחר קובץ CSV');
      return;
    }

    if (teams.length === 0) {
      toast.error(language === 'en' ? 'Please create teams first before importing games' : 'אנא צור קבוצות תחילה לפני יבוא משחקים');
      return;
    }

    setUploading(true);
    try {
      // Read file with proper UTF-8 encoding to handle Hebrew text
      const arrayBuffer = await file.arrayBuffer();
      const decoder = new TextDecoder('utf-8');
      const text = decoder.decode(arrayBuffer);
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error(language === 'en' ? 'CSV file must contain at least a header row and one data row' : 'קובץ CSV חייב להכיל לפחות שורת כותרת ושורת נתונים אחת');
        return;
      }

      // Parse CSV header with proper quote handling
      const headers = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());
      const requiredHeaders = ['hometeam', 'awayteam', 'date', 'time'];
      
      const missingHeaders = requiredHeaders.filter(header => !headers.includes(header));
      if (missingHeaders.length > 0) {
        toast.error(`${language === 'en' ? 'CSV must contain columns:' : 'CSV חייב להכיל עמודות:'} ${missingHeaders.join(', ')}`);
        return;
      }

      const games = [];
      const errors = [];

      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        if (values.length === 0 || !values[0]) continue;

        const gameData = {};
        headers.forEach((header, index) => {
          gameData[header] = values[index] || '';
        });

        // Find teams by ID or name
        const homeTeam = findTeamByIdOrName(gameData.hometeam);
        const awayTeam = findTeamByIdOrName(gameData.awayteam);

        if (!homeTeam) {
          errors.push(`Row ${i + 1}: Home team "${gameData.hometeam}" not found (searched by ID and name)`);
          continue;
        }

        if (!awayTeam) {
          errors.push(`Row ${i + 1}: Away team "${gameData.awayteam}" not found (searched by ID and name)`);
          continue;
        }

        if (homeTeam._id === awayTeam._id) {
          errors.push(`Row ${i + 1}: Home and away teams cannot be the same`);
          continue;
        }

        const normalizedDate = parseGameDateForCsv(gameData.date);
        if (!normalizedDate) {
          errors.push(
            `${language === 'en' ? 'Row' : 'שורה'} ${i + 1}: ${language === 'en' ? 'Invalid date' : 'תאריך לא תקין'} "${gameData.date}". ${language === 'en' ? 'Use dd/mm/yyyy (e.g. 15/01/2024) or YYYY-MM-DD' : 'השתמש ב-dd/mm/yyyy או YYYY-MM-DD'}`
          );
          continue;
        }

        // Validate time format
        const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(gameData.time)) {
          errors.push(`Row ${i + 1}: Invalid time format "${gameData.time}". Use HH:MM`);
          continue;
        }

        // Ensure round is not empty (server requires it)
        let roundValue = gameData.round || '';
        if (!roundValue.trim()) {
          roundValue = 'Round ' + (gameData.round || i); // Use row number as fallback
        }

        const game = {
          homeGroupId: homeTeam._id,
          awayGroupId: awayTeam._id,
          date: normalizedDate,
          hour: gameData.time,
          round: roundValue.trim()
        };

        // Store additional data for post-processing
        const additionalData = {
          isBettingLocked: gameData.bettinglocked?.toLowerCase() === 'true' || false,
          isFinished: gameData.finished?.toLowerCase() === 'true' || false,
          finalResult: ''
        };

        // Handle final result if provided
        if (gameData.homescore && gameData.awayscore) {
          if (!/^\d+$/.test(gameData.homescore) || !/^\d+$/.test(gameData.awayscore)) {
            errors.push(`Row ${i + 1}: Home and away scores must be valid numbers`);
            continue;
          }
          additionalData.finalResult = `${gameData.homescore}:${gameData.awayscore}`;
        } else if (gameData.finalresult && gameData.finalresult.trim()) {
          if (!/^\d+:\d+$/.test(gameData.finalresult.trim())) {
            errors.push(`Row ${i + 1}: Final result must be in format "homeScore:awayScore"`);
            continue;
          }
          additionalData.finalResult = gameData.finalresult.trim();
        }

        games.push({ game, additionalData });
      }

      if (errors.length > 0) {
        console.error('CSV validation errors:', errors);
        toast.error(`Found ${errors.length} error${errors.length !== 1 ? 's' : ''} in CSV. Check console for details.`);
        if (games.length === 0) return;
      }

      if (games.length === 0) {
        toast.error('No valid games found in CSV');
        return;
      }

      // Upload games to backend (create basic games first)
      const gameCreationResults = [];
      
      for (let i = 0; i < games.length; i++) {
        const { game, additionalData } = games[i];
        try {
          console.log('Creating game:', game);
          const response = await axios.post(gamesBase(), game);
          gameCreationResults.push({ 
            success: true, 
            gameId: response.data.game._id, 
            additionalData,
            index: i 
          });
        } catch (error) {
          console.error(`Failed to create game ${i + 1}:`, error.response?.data || error.message);
          gameCreationResults.push({ 
            success: false, 
            error: error.response?.data?.message || error.message,
            index: i 
          });
        }
      }
      
      const successful = gameCreationResults.filter(r => r.success).length;
      const failed = gameCreationResults.filter(r => !r.success).length;

      // Post-process games that were created successfully (update final results if needed)
      for (const result of gameCreationResults) {
        if (result.success && result.additionalData.finalResult) {
          try {
            await axios.put(`${gamesBase()}/${result.gameId}/result`, {
              finalResult: result.additionalData.finalResult
            });
            console.log(`Updated final result for game ${result.gameId}`);
          } catch (error) {
            console.error(`Failed to update final result for game ${result.gameId}:`, error.response?.data || error.message);
          }
        }
      }

      if (successful > 0) {
        toast.success(`Successfully imported ${successful} game${successful !== 1 ? 's' : ''}`);
        fetchData();
      }
      
      if (failed > 0) {
        const failedResults = gameCreationResults.filter(r => !r.success);
        console.error('Failed game imports:', failedResults);
        toast.error(`Failed to import ${failed} game${failed !== 1 ? 's' : ''}. Check console for details.`);
      }

    } catch (error) {
      console.error('Error processing CSV:', error);
      toast.error('Failed to process CSV file');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const downloadCurrentGamesCSV = () => {
    // Create header row with team IDs information
    const headers = ['homeTeam', 'awayTeam', 'date', 'time', 'round', 'bettingLocked', 'finished', 'homeScore', 'awayScore', 'finalResult'];
    
    // Convert current games to CSV format
    const gameRows = games.map(game => {
      const homeTeam = typeof game.homeGroupId === 'object' ? game.homeGroupId : {};
      const awayTeam = typeof game.awayGroupId === 'object' ? game.awayGroupId : {};
      
      // Extract scores from finalResult if available
      let homeScore = '';
      let awayScore = '';
      if (game.finalResult && typeof game.finalResult === 'string' && game.finalResult.includes(':')) {
        const scores = game.finalResult.split(':');
        homeScore = scores[0] || '';
        awayScore = scores[1] || '';
      }
      
      return [
        getTeamDisplayName(homeTeam, 'en') || '',
        getTeamDisplayName(awayTeam, 'en') || '',
        isoDateToDMYDisplay(game.date || '') || game.date || '',
        game.hour || '',
        game.round || '',
        game.isBettingLocked ? 'true' : 'false',
        game.isFinished ? 'true' : 'false',
        homeScore,
        awayScore,
        game.finalResult || ''
      ];
    });
    
    // If no games exist, provide sample data with team IDs
    const csvData = games.length > 0 ? [headers, ...gameRows] : [
      headers,
      ['Manchester United', 'Real Madrid', '15/01/2024', '20:00', 'Quarter Final', 'false', 'false', '', '', ''],
      ['Bayern Munich', 'Barcelona', '16/01/2024', '18:30', 'Quarter Final', 'false', 'true', '2', '1', ''],
    ];
    
    // Create CSV content with UTF-8 BOM for proper Hebrew support
    const BOM = '\uFEFF';
    const csvContent = BOM + csvData.map(row => 
      row.map(cell => {
        // Escape cells that contain commas, quotes, or newlines
        if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      }).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = games.length > 0 ? 'current-games.csv' : 'games-template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getGameStatus = (game) => {
    if (game.isCanceled) return { status: 'canceled', text: t('canceled'), color: '#ef4444' };
    if (game.isFinished) return { status: 'finished', text: t('finished'), color: '#10b981' };
    if (game.isBettingLocked) return { status: 'live', text: t('live'), color: '#f59e0b' };
    return { status: 'upcoming', text: t('upcoming'), color: '#dc2626' };
  };

  if (loading) {
    return (
      <AdminLayout title={t('manageGames')} subtitle={language === 'en' ? 'Schedule & results' : 'לוח ותוצאות'} loading />
    );
  }

  const handleConfirmPending = async (gameId) => {
    try {
      setPendingActionId(gameId);
      await adminConfirmPendingResult(competitionSlug, gameId);
      toast.success(
        language === 'en' ? 'Result confirmed' : 'התוצאה אושרה'
      );
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message || (language === 'en' ? 'Failed' : 'נכשל'));
    } finally {
      setPendingActionId(null);
    }
  };

  const handleDismissPending = async (gameId) => {
    try {
      setPendingActionId(gameId);
      await adminDismissPendingResult(competitionSlug, gameId);
      toast.success(
        language === 'en' ? 'Suggestion dismissed' : 'ההצעה נדחתה'
      );
      setPendingResults((prev) => prev.filter((g) => g._id !== gameId));
    } catch (err) {
      toast.error(err?.response?.data?.message || (language === 'en' ? 'Failed' : 'נכשל'));
    } finally {
      setPendingActionId(null);
    }
  };

  return (
    <AdminLayout
      title={t('manageGames')}
      subtitle={`${games.length} ${language === 'en' ? 'games' : 'משחקים'}`}
    >
        {pendingResults.length > 0 ? (
          <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50/80 p-4">
            <h3 className="mb-3 text-sm font-bold text-amber-950">
              {language === 'en'
                ? `Pending API results (${pendingResults.length})`
                : `תוצאות ממתינות לאישור (${pendingResults.length})`}
            </h3>
            <ul className="flex flex-col gap-2">
              {pendingResults.map((game) => {
                const home = getTeamDisplayName(game.homeGroupId, language);
                const away = getTeamDisplayName(game.awayGroupId, language);
                const pr = game.pendingResult || {};
                return (
                  <li
                    key={game._id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white px-3 py-2 text-sm shadow-sm"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="flex items-center gap-1.5 font-semibold text-zinc-900">
                        <span>{home}</span>
                        <span className="font-semibold text-zinc-400">vs</span>
                        <span>{away}</span>
                      </span>
                      <span className="text-zinc-500">
                        <ScoreText score={pr} className="font-semibold" />
                      </span>
                      <span className="text-xs text-zinc-400">
                        {game.date} {game.hour}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="success"
                        loading={pendingActionId === game._id}
                        onClick={() => handleConfirmPending(game._id)}
                      >
                        <CheckCircle className="h-4 w-4" />
                        {language === 'en' ? 'Confirm' : 'אשר'}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={pendingActionId === game._id}
                        onClick={() => handleDismissPending(game._id)}
                      >
                        <X className="h-4 w-4" />
                        {language === 'en' ? 'Dismiss' : 'דחה'}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
        <div className="mb-6 flex flex-wrap gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleCSVUpload}
            accept=".csv"
            className="hidden"
          />
          <Button
            variant="success"
            size="sm"
            loading={uploading}
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            <FileText className="h-4 w-4" />
            {uploading ? (language === 'en' ? 'Uploading…' : 'מעלה…') : t('importCSV')}
          </Button>
          <Button
            size="sm"
            className="!bg-amber-500 !text-white hover:!bg-amber-600"
            onClick={downloadCurrentGamesCSV}
          >
            <Download className="h-4 w-4" />
            {games.length > 0
              ? language === 'en'
                ? 'Download CSV'
                : 'הורד CSV'
              : t('downloadSample')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setEditingGame(null);
              resetForm();
              setShowForm(true);
            }}
          >
            <Plus className="h-4 w-4" />
            {t('addGame')}
          </Button>
          <Button
            size="sm"
            className="!bg-brand-600 !text-white hover:!bg-brand-700"
            onClick={() => setShowRoundForm(true)}
          >
            <Layers className="h-4 w-4" />
            {t('addRound')}
          </Button>
          <Button
            size="sm"
            className="!bg-amber-600 !text-white hover:!bg-amber-700"
            onClick={handleOpenCloseRound}
          >
            <Target className="h-4 w-4" />
            {t('closeRound')}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            loading={recalculating}
            disabled={recalculating}
            onClick={handleRecalculateResults}
          >
            <RefreshCw className="h-4 w-4" />
            {language === 'en' ? 'Recalculate points' : 'חשב נקודות מחדש'}
          </Button>
          {games.length > 0 && (
            <Button variant="secondary" size="sm" onClick={toggleSelectAllGames}>
              {allGamesSelected ? (
                <Square className="h-4 w-4" />
              ) : (
                <CheckSquare className="h-4 w-4" />
              )}
              {allGamesSelected
                ? language === 'en'
                  ? 'Deselect all'
                  : 'בטל בחירה'
                : language === 'en'
                  ? 'Select all'
                  : 'בחר הכל'}
            </Button>
          )}
          {games.length > 0 && selectedGameIds.length > 0 && (
            <Button
              variant="danger"
              size="sm"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
            >
              <Trash2 className="h-4 w-4" />
              {language === 'en'
                ? `Delete selected (${selectedGameIds.length})`
                : `מחק נבחרים (${selectedGameIds.length})`}
            </Button>
          )}
        </div>

        {/* CSV Format Guide */}
        <div style={{ background: '#fef2f2', borderRadius: 12, padding: 20, marginBottom: 32, border: '1px solid #fee2e2' }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#18181b', marginBottom: 12 }}>
            {t('csvImportGuide')}
          </h3>
          <div style={{ color: '#dc2626', fontSize: 14, lineHeight: 1.6 }}>
            <div style={{ marginBottom: 8 }}>
              <strong>{t('requiredColumns')}:</strong> homeTeam, awayTeam, date, time
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>{t('optionalColumns')}:</strong> round, bettingLocked, finished, homeScore, awayScore, finalResult
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>{language === 'en' ? 'Team Format' : 'פורמט קבוצה'}:</strong> {language === 'en' ? 'Use team name OR team ID' : 'השתמש בשם קבוצה או מזהה קבוצה'}
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>{t('dateFormat')}:</strong>{' '}
              {language === 'en'
                ? 'dd/mm/yyyy (e.g. 15/01/2024). Legacy YYYY-MM-DD still accepted on import.'
                : 'dd/mm/yyyy (למשל 15/01/2024). ייבוא ב-YYYY-MM-DD עדיין נתמך.'}{' '}
              {language === 'en' ? 'Game form auto-fills slashes as you type digits.' : 'בטופס: הקו נוסף אוטומטית בזמן הקלדה.'}
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>{t('timeFormat')}:</strong> {t('timeCsvFormatHint')}
            </div>
            
            {/* Show available teams with their IDs */}
            {teams.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <strong>{language === 'en' ? 'Available Teams & IDs:' : 'קבוצות זמינות ומזהים:'}</strong>
                <div style={{ background: '#f8fafc', padding: 12, borderRadius: 6, marginTop: 4, maxHeight: 120, overflowY: 'auto' }}>
                  {teams.map((team, index) => (
                    <div key={team._id} style={{ 
                      fontSize: 12, 
                      fontFamily: 'monospace', 
                      marginBottom: 4,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{ fontWeight: 600, color: '#18181b' }}>{getTeamDisplayName(team, language)}</span>
                      <span style={{ color: '#dc2626', fontSize: 11, background: '#fee2e2', padding: '1px 4px', borderRadius: 3 }}>
                        ID: {team._id}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div style={{ background: '#f8fafc', padding: 8, borderRadius: 6, fontFamily: 'monospace', fontSize: 13 }}>
              <div style={{ marginBottom: 4, color: '#10b981' }}>
                {language === 'en' ? '# Using team names:' : '# באמצעות שמות קבוצות:'}
              </div>
              Manchester United,Real Madrid,15/01/2024,20:00,Quarter Final,false,true,2,1,
              <div style={{ marginTop: 8, marginBottom: 4, color: '#10b981' }}>
                {language === 'en' ? '# Using full team IDs (24 characters):' : '# באמצעות מזהי קבוצות מלאים (24 תווים):'}
              </div>
              507f1f77bcf86cd799439011,507f1f77bcf86cd799439012,15/01/2024,20:00,Quarter Final,false,true,2,1,
              <div style={{ marginTop: 8, marginBottom: 4, color: '#10b981' }}>
                {language === 'en' ? '# Using partial team IDs (8+ characters):' : '# באמצעות מזהי קבוצות חלקיים (8+ תווים):'}
              </div>
              687acdc7,687acdc8,15/01/2024,20:00,Quarter Final,false,true,2,1,
              <div style={{ marginTop: 8, marginBottom: 4, color: '#f59e0b' }}>
                {language === 'en' ? '# Note: Round field is required (will auto-generate if empty)' : '# הערה: שדה סיבוב נדרש (יווצר אוטומטית אם ריק)'}
              </div>
            </div>
          </div>
        </div>

        {/* Game Form */}
        {showForm && (
          <div style={{ background: '#f8fafc', borderRadius: 16, padding: 28, marginBottom: 32, boxShadow: '0 2px 8px rgba(220, 38, 38, 0.09)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#18181b' }}>
                {editingGame ? t('editGame') : t('createNewGame')}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingGame(null);
                  resetForm();
                }}
                style={{ background: '#fee2e2', color: '#18181b', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer' }}
              >
                <X style={{ width: 20, height: 20 }} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20 }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 8, textAlign: isRTL ? 'right' : 'left' }}>
                  {t('homeTeam')}
                </label>
                <select
                  value={formData.homeGroupId}
                  onChange={(e) => setFormData({ ...formData, homeGroupId: e.target.value })}
                  required
                  style={{ width: '100%', padding: '12px', borderRadius: 8, border: '1.5px solid #fee2e2', background: 'white', color: '#18181b', fontSize: 14 }}
                >
                  <option value="">
                    {language === 'en' ? 'Select home team' : 'בחר קבוצת בית'}
                  </option>
                  {teams.map(team => (
                    <option key={team._id} value={team._id}>{getTeamDisplayName(team, language)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 8, textAlign: isRTL ? 'right' : 'left' }}>
                  {t('awayTeam')}
                </label>
                <select
                  value={formData.awayGroupId}
                  onChange={(e) => setFormData({ ...formData, awayGroupId: e.target.value })}
                  required
                  style={{ width: '100%', padding: '12px', borderRadius: 8, border: '1.5px solid #fee2e2', background: 'white', color: '#18181b', fontSize: 14 }}
                >
                  <option value="">
                    {language === 'en' ? 'Select away team' : 'בחר קבוצת חוץ'}
                  </option>
                  {teams.map(team => (
                    <option key={team._id} value={team._id}>{getTeamDisplayName(team, language)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 8, textAlign: isRTL ? 'right' : 'left' }}>
                  {t('date')}
                </label>
                <DateInputDMY
                  value={formData.date}
                  onChange={(iso) => setFormData({ ...formData, date: iso })}
                  language={language}
                  required
                  style={{ width: '100%', padding: '12px', borderRadius: 8, border: '1.5px solid #fee2e2', background: 'white', color: '#18181b', fontSize: 14 }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 8, textAlign: isRTL ? 'right' : 'left' }}>
                  {t('time')}{' '}
                  <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 400 }}>{t('time24hNote')}</span>
                </label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select
                    value={formData.hour.split(':')[0] || ''}
                    onChange={(e) => {
                      const minutes = formData.hour.split(':')[1] || '00';
                      setFormData({ ...formData, hour: `${e.target.value.padStart(2, '0')}:${minutes}` });
                    }}
                    required
                    aria-label={t('time')}
                    style={{ 
                      flex: 1,
                      padding: '12px', 
                      borderRadius: 8, 
                      border: '1.5px solid #fee2e2', 
                      background: 'white', 
                      color: '#18181b', 
                      fontSize: 14,
                      fontFamily: 'monospace'
                    }}
                  >
                    <option value="">{t('timeHourPlaceholder')}</option>
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i.toString().padStart(2, '0')}>
                        {i.toString().padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                  <span style={{ color: '#6b7280', fontSize: 18, fontWeight: 'bold' }}>:</span>
                  <select
                    value={formData.hour.split(':')[1] || ''}
                    onChange={(e) => {
                      const hours = formData.hour.split(':')[0] || '00';
                      setFormData({ ...formData, hour: `${hours}:${e.target.value.padStart(2, '0')}` });
                    }}
                    required
                    aria-label={t('timeMinAria')}
                    style={{ 
                      flex: 1,
                      padding: '12px', 
                      borderRadius: 8, 
                      border: '1.5px solid #fee2e2', 
                      background: 'white', 
                      color: '#18181b', 
                      fontSize: 14,
                      fontFamily: 'monospace'
                    }}
                  >
                    <option value="">{t('timeMinPlaceholder')}</option>
                    {Array.from({ length: 60 }, (_, i) => (
                      <option key={i} value={i.toString().padStart(2, '0')}>
                        {i.toString().padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, textAlign: isRTL ? 'right' : 'left' }}>
                  {t('timeFieldHelp')}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 8, textAlign: isRTL ? 'right' : 'left' }}>
                  {t('round')}
                </label>
                <input
                  type="text"
                  value={formData.round}
                  onChange={(e) => setFormData({ ...formData, round: e.target.value })}
                  placeholder={language === 'en' ? 'e.g., Quarter Final, Semi Final' : 'לדוגמה, רבע גמר, חצי גמר'}
                  style={{ width: '100%', padding: '12px', borderRadius: 8, border: '1.5px solid #fee2e2', background: 'white', color: '#18181b', fontSize: 14 }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 8, textAlign: isRTL ? 'right' : 'left' }}>
                  🏠 {t('homeTeamScore')}
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.homeScore}
                  onChange={(e) => setFormData({ ...formData, homeScore: e.target.value })}
                  placeholder="0"
                  style={{ width: '100%', padding: '12px', borderRadius: 8, border: '1.5px solid #fee2e2', background: 'white', color: '#18181b', fontSize: 14 }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 8, textAlign: isRTL ? 'right' : 'left' }}>
                  ✈️ {t('awayTeamScore')}
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.awayScore}
                  onChange={(e) => setFormData({ ...formData, awayScore: e.target.value })}
                  placeholder="0"
                  style={{ width: '100%', padding: '12px', borderRadius: 8, border: '1.5px solid #fee2e2', background: 'white', color: '#18181b', fontSize: 14 }}
                />
                <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>
                  {language === 'en' ? 'Leave both scores empty for upcoming games' : 'השאר את שתי התוצאות ריקות למשחקים עתידיים'}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.isBettingLocked}
                    onChange={(e) => setFormData({ ...formData, isBettingLocked: e.target.checked })}
                    style={{ width: 16, height: 16 }}
                  />
                  <span style={{ fontWeight: 600, color: '#18181b', fontSize: 14 }}>
                    {t('bettingLocked')}
                  </span>
                </label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.isFinished}
                    onChange={(e) => setFormData({ ...formData, isFinished: e.target.checked })}
                    style={{ width: 16, height: 16 }}
                  />
                  <span style={{ fontWeight: 600, color: '#18181b', fontSize: 14 }}>
                    {t('gameFinished')}
                  </span>
                </label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.isCanceled}
                    onChange={(e) => setFormData({ ...formData, isCanceled: e.target.checked })}
                    style={{ width: 16, height: 16 }}
                  />
                  <span style={{ fontWeight: 600, color: '#18181b', fontSize: 14 }}>
                    {t('cancelGame')}
                  </span>
                </label>
                <div style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>
                  {t('cancelGameDesc')}
                </div>
              </div>

              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingGame(null);
                    resetForm();
                  }}
                  style={{ background: '#fee2e2', color: '#18181b', fontWeight: 600, fontSize: 15, padding: '12px 24px', borderRadius: 10, border: 'none', cursor: 'pointer' }}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  style={{ background: 'linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)', color: 'white', fontWeight: 600, fontSize: 15, padding: '12px 24px', borderRadius: 10, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 2px 8px rgba(220, 38, 38, 0.15)' }}
                >
                  <Save style={{ width: 18, height: 18 }} />
                  {editingGame ? t('updateGame') : t('createGame')}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Quick Links to Game Pages */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, marginBottom: 32 }}>
          <div 
            onClick={() => navigate(`${basePath}/upcoming-games`)}
            style={{ 
              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)', 
              color: 'white', 
              borderRadius: 16, 
              padding: 24, 
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.20)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.30)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.20)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <Calendar style={{ width: 24, height: 24 }} />
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
                {language === 'en' ? 'Upcoming Games' : 'משחקים עתידיים'}
              </h3>
            </div>
            <p style={{ margin: 0, opacity: 0.9, fontSize: 14 }}>
              {language === 'en' 
                ? 'View and manage upcoming games available for betting' 
                : 'צפה ונהל משחקים עתידיים זמינים להימורים'}
            </p>
          </div>
          
          <div 
            onClick={() => navigate(`${basePath}/completed-games`)}
            style={{ 
              background: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)', 
              color: 'white', 
              borderRadius: 16, 
              padding: 24, 
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.20)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(99, 102, 241, 0.30)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.20)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <CheckCircle style={{ width: 24, height: 24 }} />
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
                {language === 'en' ? 'Completed Games' : 'משחקים שהסתיימו'}
              </h3>
            </div>
            <p style={{ margin: 0, opacity: 0.9, fontSize: 14 }}>
              {language === 'en' 
                ? 'View finished games with results and betting outcomes' 
                : 'צפה במשחקים שהסתיימו עם תוצאות ותוצאות הימורים'}
            </p>
          </div>
        </div>

        {/* Recent Games Summary */}
        <div style={{ background: '#f8fafc', borderRadius: 16, padding: 24, marginBottom: 32, boxShadow: '0 2px 8px rgba(220, 38, 38, 0.09)' }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#18181b', marginBottom: 16 }}>
            {language === 'en' ? 'Games Overview' : 'סקירת משחקים'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#10b981', marginBottom: 4 }}>
                {games.filter(g => !g.isFinished).length}
              </div>
              <div style={{ fontSize: 14, color: '#6b7280' }}>
                {language === 'en' ? 'Upcoming' : 'עתידיים'}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#dc2626', marginBottom: 4 }}>
                {games.filter(g => g.isFinished).length}
              </div>
              <div style={{ fontSize: 14, color: '#6b7280' }}>
                {language === 'en' ? 'Completed' : 'הסתיימו'}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#f59e0b', marginBottom: 4 }}>
                {games.filter(g => g.isBettingLocked && !g.isFinished).length}
              </div>
              <div style={{ fontSize: 14, color: '#6b7280' }}>
                {language === 'en' ? 'Live' : 'בשידור חי'}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#18181b', marginBottom: 4 }}>
                {games.length}
              </div>
              <div style={{ fontSize: 14, color: '#6b7280' }}>
                {language === 'en' ? 'Total' : 'סה"כ'}
              </div>
            </div>
          </div>
        </div>

        {/* Games Management Section */}
        <div style={{ background: '#f8fafc', borderRadius: 16, padding: 24, marginBottom: 32, boxShadow: '0 2px 8px rgba(220, 38, 38, 0.09)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#18181b', margin: 0 }}>
              {language === 'en' ? 'Manage Games' : 'נהל משחקים'}
            </h3>
            <div style={{ color: '#6b7280', fontSize: 14 }}>
              {games.length} {language === 'en' ? 'games shown' : 'משחקים מוצגים'} / {allGames.length} {language === 'en' ? 'total' : 'סה"כ'}
            </div>
          </div>

          {/* Filters */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: 20, 
            padding: '12px 16px', 
            background: 'white', 
            borderRadius: 8, 
            border: '1px solid #e2e8f0',
            flexWrap: 'wrap',
            gap: 12
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Filter style={{ width: 16, height: 16, color: '#dc2626' }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: '#18181b' }}>
                  {language === 'en' ? 'Filters:' : 'מסננים:'}
                </span>
              </div>
              
              {/* Round Dropdown */}
              <div style={{ position: 'relative' }}>
                <select
                  value={selectedRound}
                  onChange={(e) => setSelectedRound(e.target.value)}
                  style={{
                    background: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    padding: '6px 28px 6px 10px',
                    fontSize: 13,
                    fontWeight: 500,
                    color: '#374151',
                    cursor: 'pointer',
                    appearance: 'none',
                    minWidth: 120
                  }}
                >
                  {availableRounds.map(round => (
                    <option key={round.value} value={round.value}>
                      {round.label}
                    </option>
                  ))}
                </select>
                <ChevronDown 
                  style={{ 
                    position: 'absolute', 
                    right: 6, 
                    top: '50%', 
                    transform: 'translateY(-50%)', 
                    width: 14, 
                    height: 14, 
                    color: '#6b7280',
                    pointerEvents: 'none'
                  }} 
                />
              </div>
              
              {/* Close Games Toggle */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showCloseGamesOnly}
                  onChange={(e) => setShowCloseGamesOnly(e.target.checked)}
                  style={{ 
                    width: 14, 
                    height: 14, 
                    accentColor: '#dc2626',
                    cursor: 'pointer'
                  }}
                />
                <span style={{ fontSize: 13, color: '#374151' }}>
                  {language === 'en' ? 'Close games only (5 days)' : 'משחקים קרובים בלבד (5 ימים)'}
                </span>
              </label>
            </div>
            
            {/* Active Filter Indicator */}
            {(selectedRound !== 'all' || showCloseGamesOnly) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: '#6b7280' }}>
                  {language === 'en' ? 'Active filter:' : 'מסנן פעיל:'}
                </span>
                <span style={{ 
                  background: '#fee2e2', 
                  color: '#dc2626', 
                  borderRadius: 4, 
                  padding: '1px 6px', 
                  fontSize: 11, 
                  fontWeight: 600 
                }}>
                  {selectedRound === 'all' && showCloseGamesOnly
                    ? (language === 'en' ? 'Close Games' : 'משחקים קרובים')
                    : availableRounds.find(r => r.value === selectedRound)?.label || selectedRound
                  }
                </span>
              </div>
            )}
          </div>
          
          {games.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <Button variant="secondary" size="sm" onClick={toggleSelectAllGames}>
                {allGamesSelected ? (
                  <Square className="h-4 w-4" />
                ) : (
                  <CheckSquare className="h-4 w-4" />
                )}
                {allGamesSelected
                  ? language === 'en'
                    ? 'Deselect all'
                    : 'בטל בחירה'
                  : language === 'en'
                    ? 'Select all'
                    : 'בחר הכל'}
              </Button>
              {selectedGameIds.length > 0 && (
                <span className="text-sm text-slate-600">
                  {language === 'en'
                    ? `${selectedGameIds.length} selected`
                    : `${selectedGameIds.length} נבחרו`}
                </span>
              )}
            </div>
          )}

          {games.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxHeight: 400, overflowY: 'auto' }}>
              {games.map((game) => {
                const homeTeam = typeof game.homeGroupId === 'object' ? game.homeGroupId : {};
                const awayTeam = typeof game.awayGroupId === 'object' ? game.awayGroupId : {};
                const gameStatus = getGameStatus(game);

                return (
                  <div key={game._id} style={{ 
                    background: selectedGameIds.includes(game._id) ? '#fef2f2' : 'white', 
                    borderRadius: 12, 
                    padding: 16, 
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    border: selectedGameIds.includes(game._id) ? '1px solid #fca5a5' : '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flexShrink: 0 }}>
                        <input
                          type="checkbox"
                          checked={selectedGameIds.includes(game._id)}
                          onChange={() => toggleGameSelection(game._id)}
                          style={{ width: 16, height: 16, accentColor: '#dc2626', cursor: 'pointer' }}
                        />
                      </label>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <img
                              src={homeTeam.logoUrl || '/default-team.png'}
                              alt={getTeamDisplayName(homeTeam, language)}
                              style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }}
                            />
                            <span style={{ fontWeight: 600, color: '#18181b', fontSize: 14 }}>{getTeamDisplayName(homeTeam, language)}</span>
                          </div>
                          <span style={{ fontSize: 14, fontWeight: 600, color: '#dc2626' }}>vs</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontWeight: 600, color: '#18181b', fontSize: 14 }}>{getTeamDisplayName(awayTeam, language)}</span>
                            <img
                              src={awayTeam.logoUrl || '/default-team.png'}
                              alt={getTeamDisplayName(awayTeam, language)}
                              style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }}
                            />
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#dc2626', fontSize: 12 }}>
                            <Calendar style={{ width: 12, height: 12 }} />
                            <span>{dayjs(game.date + ' ' + game.hour).format('DD/MM/YYYY HH:mm')}</span>
                          </div>
                          {game.round && (
                            <span style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 600 }}>
                              {game.round}
                            </span>
                          )}
                          <span style={{ background: gameStatus.color + '20', color: gameStatus.color, borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 600 }}>
                            {gameStatus.text}
                          </span>
                          {game.isBettingLocked && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 2, color: '#f59e0b', fontSize: 10 }}>
                              <Lock style={{ width: 10, height: 10 }} />
                              <span>{t('bettingLocked')}</span>
                            </div>
                          )}
                          {game.isCanceled && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 2, color: '#ef4444', fontSize: 10 }}>
                              <X style={{ width: 10, height: 10 }} />
                              <span>
                                {t('canceled')}: <ScoreText score={game.finalResult} />
                              </span>
                            </div>
                          )}
                          {game.isFinished && !game.isCanceled && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 2, color: '#10b981', fontSize: 10 }}>
                              <CheckCircle style={{ width: 10, height: 10 }} />
                              <span>
                                {t('finished')}: <ScoreText score={game.finalResult} />
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => handleEdit(game)}
                          style={{ 
                            background: 'linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)', 
                            color: 'white', 
                            fontWeight: 600, 
                            fontSize: 12, 
                            padding: '6px 12px', 
                            borderRadius: 6, 
                            border: 'none', 
                            cursor: 'pointer', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 4, 
                            boxShadow: '0 1px 3px rgba(220, 38, 38, 0.15)' 
                          }}
                        >
                          <Edit style={{ width: 12, height: 12 }} />
                          {t('edit')}
                        </button>
                        <button
                          onClick={() => handleDelete(game._id)}
                          style={{ 
                            background: '#ef4444', 
                            color: 'white', 
                            fontWeight: 600, 
                            fontSize: 12, 
                            padding: '6px 12px', 
                            borderRadius: 6, 
                            border: 'none', 
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4
                          }}
                        >
                          <X style={{ width: 12, height: 12 }} />
                          {t('delete')}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ background: 'white', borderRadius: 12, padding: 32, textAlign: 'center', color: '#a1a1aa', fontSize: 16 }}>
              <Calendar style={{ width: 32, height: 32, color: '#fee2e2', marginBottom: 12 }} />
              <div>{language === 'en' ? 'No games created yet' : 'אין משחקים שנוצרו עדיין'}</div>
              <div style={{ color: '#dc2626', fontSize: 13, marginTop: 6 }}>
                {language === 'en' ? 'Create your first game to get started!' : 'צור את המשחק הראשון שלך כדי להתחיל!'}
              </div>
            </div>
          )}
        </div>

        {/* Add Round Modal */}
        {showRoundForm && (
          <div style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(0, 0, 0, 0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{ 
              background: 'white', 
              borderRadius: 16, 
              padding: 32, 
              width: '100%', 
              maxWidth: 900,
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h2 style={{ fontSize: 24, fontWeight: 700, color: '#18181b' }}>
                  {t('addRound')}
                </h2>
                <button
                  onClick={() => {
                    setShowRoundForm(false);
                    setRoundFormData({
                      roundName: '',
                      games: Array(7).fill(null).map(() => ({
                        homeGroupId: '',
                        awayGroupId: '',
                        date: '',
                        hour: '',
                        isBettingLocked: false
                      }))
                    });
                  }}
                  style={{ background: '#fee2e2', color: '#18181b', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer' }}
                >
                  <X style={{ width: 20, height: 20 }} />
                </button>
              </div>
              
              <form onSubmit={handleRoundSubmit}>
                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 8 }}>
                    {t('roundName')} *
                  </label>
                  <input
                    type="text"
                    value={roundFormData.roundName}
                    onChange={(e) => setRoundFormData({ ...roundFormData, roundName: e.target.value })}
                    placeholder={language === 'en' ? 'e.g., Round 1, Quarter Final' : 'לדוגמה, סיבוב 1, רבע גמר'}
                    required
                    style={{ width: '100%', padding: '12px', borderRadius: 8, border: '1.5px solid #fee2e2', background: 'white', color: '#18181b', fontSize: 14 }}
                  />
                </div>

                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 600, color: '#18181b', marginBottom: 16 }}>
                    {language === 'en' ? '7 Games' : '7 משחקים'}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {roundFormData.games.map((game, index) => (
                      <div key={index} style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #fee2e2' }}>
                        <h4 style={{ fontSize: 14, fontWeight: 600, color: '#dc2626', marginBottom: 12 }}>
                          {language === 'en' ? `Game ${index + 1}` : `משחק ${index + 1}`}
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                          <div>
                            <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 4, fontSize: 12 }}>
                              {t('homeTeam')} *
                            </label>
                            <select
                              value={game.homeGroupId}
                              onChange={(e) => handleRoundFormChange(index, 'homeGroupId', e.target.value)}
                              required
                              style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #fee2e2', background: 'white', color: '#18181b', fontSize: 13 }}
                            >
                              <option value="">{language === 'en' ? 'Select' : 'בחר'}</option>
                              {teams.map(team => (
                                <option key={team._id} value={team._id}>{getTeamDisplayName(team, language)}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 4, fontSize: 12 }}>
                              {t('awayTeam')} *
                            </label>
                            <select
                              value={game.awayGroupId}
                              onChange={(e) => handleRoundFormChange(index, 'awayGroupId', e.target.value)}
                              required
                              style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #fee2e2', background: 'white', color: '#18181b', fontSize: 13 }}
                            >
                              <option value="">{language === 'en' ? 'Select' : 'בחר'}</option>
                              {teams.map(team => (
                                <option key={team._id} value={team._id}>{getTeamDisplayName(team, language)}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 4, fontSize: 12 }}>
                              {t('date')} *
                            </label>
                            <DateInputDMY
                              value={game.date}
                              onChange={(iso) => handleRoundFormChange(index, 'date', iso)}
                              language={language}
                              required
                              style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #fee2e2', background: 'white', color: '#18181b', fontSize: 13 }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 4, fontSize: 12 }}>
                              {t('time')}{' '}
                              <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 400 }}>
                                {t('time24hNote')} *
                              </span>
                            </label>
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                              <select
                                value={game.hour.split(':')[0] || ''}
                                onChange={(e) => {
                                  const minutes = game.hour.split(':')[1] || '00';
                                  handleRoundFormChange(index, 'hour', `${e.target.value.padStart(2, '0')}:${minutes}`);
                                }}
                                required
                                aria-label={t('time')}
                                style={{ 
                                  flex: 1,
                                  padding: '8px', 
                                  borderRadius: 6, 
                                  border: '1px solid #fee2e2', 
                                  background: 'white', 
                                  color: '#18181b', 
                                  fontSize: 12,
                                  fontFamily: 'monospace'
                                }}
                              >
                                <option value="">{t('timeHourPlaceholder')}</option>
                                {Array.from({ length: 24 }, (_, i) => (
                                  <option key={i} value={i.toString().padStart(2, '0')}>
                                    {i.toString().padStart(2, '0')}
                                  </option>
                                ))}
                              </select>
                              <span style={{ color: '#6b7280', fontSize: 14, fontWeight: 'bold' }}>:</span>
                              <select
                                value={game.hour.split(':')[1] || ''}
                                onChange={(e) => {
                                  const hours = game.hour.split(':')[0] || '00';
                                  handleRoundFormChange(index, 'hour', `${hours}:${e.target.value.padStart(2, '0')}`);
                                }}
                                required
                                aria-label={t('timeMinAria')}
                                style={{ 
                                  flex: 1,
                                  padding: '8px', 
                                  borderRadius: 6, 
                                  border: '1px solid #fee2e2', 
                                  background: 'white', 
                                  color: '#18181b', 
                                  fontSize: 12,
                                  fontFamily: 'monospace'
                                }}
                              >
                                <option value="">{t('timeMinPlaceholder')}</option>
                                {Array.from({ length: 60 }, (_, i) => (
                                  <option key={i} value={i.toString().padStart(2, '0')}>
                                    {i.toString().padStart(2, '0')}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={game.isBettingLocked}
                                onChange={(e) => handleRoundFormChange(index, 'isBettingLocked', e.target.checked)}
                                style={{ width: 14, height: 14 }}
                              />
                              <span style={{ fontSize: 12, color: '#18181b' }}>
                                {t('bettingLocked')}
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowRoundForm(false);
                      setRoundFormData({
                        roundName: '',
                        games: Array(7).fill(null).map(() => ({
                          homeGroupId: '',
                          awayGroupId: '',
                          date: '',
                          hour: '',
                          isBettingLocked: false
                        }))
                      });
                    }}
                    style={{ background: '#fee2e2', color: '#18181b', fontWeight: 600, fontSize: 15, padding: '12px 24px', borderRadius: 10, border: 'none', cursor: 'pointer' }}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="submit"
                    style={{ background: 'linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)', color: 'white', fontWeight: 600, fontSize: 15, padding: '12px 24px', borderRadius: 10, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <Layers style={{ width: 18, height: 18 }} />
                    {language === 'en' ? 'Create Round' : 'צור סיבוב'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Close Round Modal */}
        {showCloseRoundModal && (
          <div style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(0, 0, 0, 0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{ 
              background: 'white', 
              borderRadius: 16, 
              padding: 32, 
              width: '100%', 
              maxWidth: 800,
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h2 style={{ fontSize: 24, fontWeight: 700, color: '#18181b' }}>
                  {t('closeRound')}
                </h2>
                <button
                  onClick={() => {
                    setShowCloseRoundModal(false);
                    setCloseRoundData({ selectedRound: '', games: [] });
                  }}
                  style={{ background: '#fee2e2', color: '#18181b', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer' }}
                >
                  <X style={{ width: 20, height: 20 }} />
                </button>
              </div>
              
              <form onSubmit={handleCloseRoundSubmit}>
                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 8 }}>
                    {t('selectRound')} *
                  </label>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <select
                      value={closeRoundData.selectedRound}
                      onChange={(e) => setCloseRoundData({ ...closeRoundData, selectedRound: e.target.value, games: [] })}
                      required
                      style={{ flex: 1, padding: '12px', borderRadius: 8, border: '1.5px solid #fee2e2', background: 'white', color: '#18181b', fontSize: 14 }}
                    >
                      <option value="">{language === 'en' ? 'Select a round' : 'בחר סיבוב'}</option>
                      {getUniqueRounds(allGames).map(round => (
                        <option key={round} value={round}>{round}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleLoadRoundGames}
                      disabled={!closeRoundData.selectedRound}
                      style={{ 
                        background: closeRoundData.selectedRound ? 'linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)' : '#fee2e2', 
                        color: closeRoundData.selectedRound ? 'white' : '#6b7280', 
                        fontWeight: 600, 
                        fontSize: 14, 
                        padding: '12px 24px', 
                        borderRadius: 8, 
                        border: 'none', 
                        cursor: closeRoundData.selectedRound ? 'pointer' : 'not-allowed' 
                      }}
                    >
                      {language === 'en' ? 'Load Games' : 'טען משחקים'}
                    </button>
                  </div>
                </div>

                {closeRoundData.games.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <h3 style={{ fontSize: 18, fontWeight: 600, color: '#18181b', marginBottom: 16 }}>
                      {t('roundGames')} ({closeRoundData.games.length})
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {closeRoundData.games.map((game, index) => (
                        <div key={game._id} style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #fee2e2' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <div>
                              <div style={{ fontSize: 14, fontWeight: 600, color: '#18181b' }}>
                                {game.homeTeam} vs {game.awayTeam}
                              </div>
                              {game.isFinished && (
                                <div style={{ fontSize: 12, color: '#10b981', marginTop: 4 }}>
                                  {t('finished')}
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={game.isFinished}
                                  onChange={(e) => handleCloseRoundScoresChange(index, 'isFinished', e.target.checked)}
                                  style={{ width: 14, height: 14 }}
                                />
                                <span style={{ fontSize: 12, color: '#18181b' }}>
                                  {t('gameFinished')}
                                </span>
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={game.isCanceled || false}
                                  onChange={(e) => handleCloseRoundScoresChange(index, 'isCanceled', e.target.checked)}
                                  style={{ width: 14, height: 14 }}
                                />
                                <span style={{ fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                                  {t('cancelGame')}
                                </span>
                              </label>
                              <div style={{ fontSize: 10, color: '#6b7280', fontStyle: 'italic', textAlign: 'right', maxWidth: 150 }}>
                                {t('cancelGameDesc')}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, alignItems: 'center' }}>
                            <div>
                              <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 4, fontSize: 12 }}>
                                {t('homeTeamScore')}
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={game.homeScore}
                                onChange={(e) => handleCloseRoundScoresChange(index, 'homeScore', e.target.value)}
                                placeholder="0"
                                style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #fee2e2', background: 'white', color: '#18181b', fontSize: 14 }}
                              />
                            </div>
                            <span style={{ fontSize: 20, fontWeight: 700, color: '#dc2626' }}>-</span>
                            <div>
                              <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 4, fontSize: 12 }}>
                                {t('awayTeamScore')}
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={game.awayScore}
                                onChange={(e) => handleCloseRoundScoresChange(index, 'awayScore', e.target.value)}
                                placeholder="0"
                                style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #fee2e2', background: 'white', color: '#18181b', fontSize: 14 }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {closeRoundData.games.length === 0 && closeRoundData.selectedRound && (
                  <div style={{ textAlign: 'center', padding: 32, color: '#6b7280' }}>
                    {t('noGamesInRound')}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCloseRoundModal(false);
                      setCloseRoundData({ selectedRound: '', games: [] });
                    }}
                    style={{ background: '#fee2e2', color: '#18181b', fontWeight: 600, fontSize: 15, padding: '12px 24px', borderRadius: 10, border: 'none', cursor: 'pointer' }}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={closeRoundData.games.length === 0}
                    style={{ 
                      background: closeRoundData.games.length > 0 ? 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)' : '#fee2e2', 
                      color: closeRoundData.games.length > 0 ? 'white' : '#6b7280', 
                      fontWeight: 600, 
                      fontSize: 15, 
                      padding: '12px 24px', 
                      borderRadius: 10, 
                      border: 'none', 
                      cursor: closeRoundData.games.length > 0 ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    <Target style={{ width: 18, height: 18 }} />
                    {t('updateAllScores')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
    </AdminLayout>
  );
};

export default AdminGames; 